import React, { useState, useRef, useEffect } from "react";
import Select from "react-select";
import AsyncSelect from "react-select/async";
import CreatableSelect from "react-select/creatable";
import { customFetch } from '../apiClient';
import PrescriptionPrint from "./PrescriptionPrint"; 

// 🎨 Theme Select Styles
const selectStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: '26px',
    height: '26px',
    fontSize: '11px',
    borderRadius: '4px',
    borderColor: state.isFocused ? 'var(--theme-color, #0088cc)' : '#cbd5e0',
    boxShadow: state.isFocused ? '0 0 0 2px color-mix(in srgb, var(--theme-color, #0088cc), transparent 85%)' : 'none',
    '&:hover': { borderColor: 'var(--theme-color, #0088cc)' },
    padding: '0'
  }),
  valueContainer: (base) => ({ ...base, padding: '0 6px', height: '24px' }),
  indicatorsContainer: (base) => ({ ...base, height: '24px' }),
  dropdownIndicator: (base) => ({ ...base, padding: '0 4px' }),
  clearIndicator: (base) => ({ ...base, padding: '0 4px' }),
  input: (base) => ({ ...base, margin: '0', padding: '0', fontSize: '11px' }),
  menu: (base) => ({ ...base, zIndex: 99999, borderRadius: '4px' }),
  menuPortal: (base) => ({ ...base, zIndex: 99999 }),
  option: (base, state) => ({
    ...base,
    fontSize: '11px',
    padding: '6px 10px',
    backgroundColor: state.isSelected
      ? 'var(--theme-color, #0088cc)'
      : state.isFocused
        ? 'color-mix(in srgb, var(--theme-color, #0088cc), white 90%)'
        : 'white',
    color: state.isSelected ? 'white' : '#333',
    cursor: 'pointer',
    '&:active': { backgroundColor: 'var(--theme-color, #0088cc)', color: 'white' }
  }),
  placeholder: (base) => ({ ...base, color: '#a0aec0', fontSize: '11px' })
};

const dosageOptions = [
  { value: "1-0-1", label: "1-0-1 (BD - Twice a day)" },
  { value: "1-1-1", label: "1-1-1 (TDS - Thrice a day)" },
  { value: "1-0-0", label: "1-0-0 (OD - Morning)" },
  { value: "0-0-1", label: "0-0-1 (OD - Night)" },
  { value: "SOS", label: "SOS (As needed)" },
  { value: "STAT", label: "STAT (Immediately)" }
];

const durationOptions = [
  { value: "3 Days", label: "3 Days" },
  { value: "5 Days", label: "5 Days" },
  { value: "1 Week", label: "1 Week" },
  { value: "15 Days", label: "15 Days" },
  { value: "1 Month", label: "1 Month" }
];

const instructionOptions = [
  { value: "After Meal", label: "After Meal (खाने के बाद)" },
  { value: "Before Meal", label: "Before Meal (खाली पेट)" },
  { value: "At Bedtime", label: "At Bedtime (सोते समय)" },
  { value: "With Warm Water", label: "With Warm Water (गर्म पानी से)" }
];

const PrescriptionCreate = () => {
  const [selectedPatient, setSelectedPatient] = useState(null);
  
  const [clinicalInfo, setClinicalInfo] = useState({
    chiefComplaints: "",
    diagnosis: "",
    notes: ""
  });

  const [currentMedicine, setCurrentMedicine] = useState({
    selectedMed: null,
    dosage: "",
    duration: "",
    instructions: ""
  });

  const [medicineCart, setMedicineCart] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const [showPrintModal, setShowPrintModal] = useState(false);
  const [printData, setPrintData] = useState(null);
  const [companyDetails, setCompanyDetails] = useState(null);

  // 👈 PANEL STATES (Tabs & History)
  const [activePanelTab, setActivePanelTab] = useState('today'); // Default is 'today'
  const [patientHistory, setPatientHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  
  const [todaysPatients, setTodaysPatients] = useState([]);
  const [loadingTodays, setLoadingTodays] = useState(false);
  
  // ✅ SEARCHING TODAY'S PATIENTS
  const [todaysSearchText, setTodaysSearchText] = useState("");

  // ✅ EXPIRY TAB STATES
  const [allExpiryData, setAllExpiryData] = useState([]);
  const [expiryDaysFilter, setExpiryDaysFilter] = useState(60); // Default 60 days
  const [expiryLoading, setExpiryLoading] = useState(false);

  const formRef = useRef(null);
  const token = sessionStorage.getItem("token");
  const loggedInDoctorId = sessionStorage.getItem("DoctorId") || 1; 
  const loggedInDoctorName = sessionStorage.getItem("DoctorName") || "Dr. Authorized";

  useEffect(() => {
    const hospitalId = sessionStorage.getItem("hospitalId");
    if (hospitalId) {
      customFetch(`/Company/${hospitalId}`)
        .then(res => res.json())
        .then(data => setCompanyDetails(data))
        .catch(console.error);
    }
  }, []);

  // ✅ FETCH EXPIRY DATA
  const fetchExpiryData = async () => {
    try {
      setExpiryLoading(true);
      const res = await customFetch(`/ExpiryReports/near-expiry`);
      if (res.ok) {
        const data = await res.json();
        setAllExpiryData(data);
      }
    } catch (err) {
      console.error("Failed to load expiry data", err);
    } finally {
      setExpiryLoading(false);
    }
  };

  // ✅ FETCH TODAY'S PATIENTS
  const fetchTodaysPatients = async () => {
    setLoadingTodays(true);
    try {
        const todayStr = new Date().toISOString().split('T')[0]; 
        
        const opdPromise = customFetch(`/Opds/list?useDateFilter=true&from=${todayStr}&to=${todayStr}&pageSize=100`)
            .then(res => res.json()).then(d => d.data || []).catch(() => []);
            
        const ipdPromise = customFetch(`/Ipds/search?useDateFilter=true&from=${todayStr}&to=${todayStr}&pageSize=100`)
            .then(res => res.json()).then(d => d.data || []).catch(() => []);

        const [opdData, ipdData] = await Promise.all([opdPromise, ipdPromise]);

        const opds = opdData.map(item => ({ ...item, _type: 'OPD', _refId: item.opdId, _srNo: item.opD_SRNO, _date: item.opdDate || item.regDate }));
        const ipds = ipdData.map(item => ({ ...item, _type: 'IPD', _refId: item.ipD_ID, _srNo: item.ipD_SRNO, _date: item.ipD_Date || item.regDate }));

        const combined = [...opds, ...ipds].sort((a, b) => new Date(b._date) - new Date(a._date));
        setTodaysPatients(combined);
    } catch (err) {
        console.error("Failed to load today's patients", err);
    } finally {
        setLoadingTodays(false);
    }
  };

  useEffect(() => {
    fetchTodaysPatients();
    fetchExpiryData(); // Fetch expiry data in background
  }, []);

  // ✅ FILTER TODAY'S PATIENTS BY SEARCH TEXT (Name or UHID)
  const filteredTodaysPatients = todaysPatients.filter(p => {
      const search = todaysSearchText.toLowerCase();
      const name = (p.name || "").toLowerCase();
      const uhid = (p.uhidNo || p.uhiD_No || "").toLowerCase();
      return name.includes(search) || uhid.includes(search);
  });

  // ✅ FILTER EXPIRY DATA BY DROPDOWN SELECTION
  const filteredExpiryList = allExpiryData.filter(item => {
    const daysLeft = item.daysRemaining ?? item.DaysRemaining;
    return daysLeft !== undefined && daysLeft <= expiryDaysFilter;
  }).sort((a, b) => (a.daysRemaining ?? a.DaysRemaining) - (b.daysRemaining ?? b.DaysRemaining));

  useEffect(() => {
    if (success) { const t = setTimeout(() => setSuccess(""), 3000); return () => clearTimeout(t); }
  }, [success]);

  useEffect(() => {
    if (error) { const t = setTimeout(() => setError(""), 4000); return () => clearTimeout(t); }
  }, [error]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key !== 'Enter') return;
      if (e.target.closest('.react-select__menu')) return;
      if (showPrintModal) return; 

      if (e.target.tagName === 'TEXTAREA' && e.shiftKey) {
          return;
      }

      if (document.activeElement.classList.contains('dc-btn-submit')) {
        e.preventDefault(); 
        document.activeElement.click(); 
        return;
      }

      e.preventDefault(); 

      const focusableSelector = 
        'input:not([disabled]):not([type="hidden"]):not([readonly]), ' +
        'select:not([disabled]), ' +
        'textarea:not([disabled]):not([readonly]), ' +
        'button:not([disabled])';

      const focusableElements = Array.from(formRef.current?.querySelectorAll(focusableSelector) || []);
      if (focusableElements.length === 0) return;
      const currentIndex = focusableElements.indexOf(document.activeElement);

      if (document.activeElement.id === 'btnAddMedicine') {
        document.activeElement.click();
        setTimeout(() => {
           const medInput = formRef.current?.querySelector('#medSearch input');
           if(medInput) medInput.focus();
        }, 100);
        return;
      }

      if (currentIndex >= 0 && currentIndex < focusableElements.length - 1) {
        focusableElements[currentIndex + 1]?.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [showPrintModal]);

  const loadPatientOptions = async (inputValue) => {
    if (!inputValue || inputValue.length < 2) return [];
    try {
      const todayStr = new Date().toISOString().split('T')[0]; 
      
      const ipdPromise = customFetch(`/Ipds/list?search=${encodeURIComponent(inputValue)}`).then(res => res.json()).catch(() => []);
      const opdPromise = customFetch(`/Opds/list?search=${encodeURIComponent(inputValue)}&useDateFilter=false`).then(res => res.json()).then(d => d.data || []).catch(() => []);

      const [ipdData, opdData] = await Promise.all([ipdPromise, opdPromise]);

      const todayIpdOptions = (Array.isArray(ipdData) ? ipdData : [])
        .filter(item => {
            const dateStr = item.regDate || item.ipD_Date;
            return dateStr && dateStr.startsWith(todayStr);
        })
        .map(item => ({
          label: `[IPD] ${item.name} | UHID: ${item.uhiD_No || item.uhidNo} | Mob: ${item.mobileNo}`,
          value: item.uhiD_No || item.uhidNo,
          originalData: { ...item, type: 'IPD', refId: item.ipD_ID, srNo: item.ipD_SRNO }
        }));

      const todayOpdOptions = (Array.isArray(opdData) ? opdData : [])
        .filter(item => {
            const dateStr = item.regDate || item.opdDate;
            return dateStr && dateStr.startsWith(todayStr);
        })
        .map(item => ({
          label: `[OPD] ${item.name} | UHID: ${item.uhidNo || item.uhiD_No} | Mob: ${item.mobileNo}`,
          value: item.uhidNo || item.uhiD_No,
          originalData: { ...item, type: 'OPD', refId: item.opdId, srNo: item.opD_SRNO }
        }));

      return [...todayIpdOptions, ...todayOpdOptions];
    } catch { return []; }
  };

  const loadMedicineOptions = async (inputValue) => {
    if (!inputValue || inputValue.length < 2) return [];
    try {
     const res = await customFetch(`/MedicineDT/search?medicine=${inputValue}`);
      const data = await res.json();
      return data.map(m => ({
        label: m.medicine,
        value: m.medID,
        medName: m.medicine
      }));
    } catch { return []; }
  };

  const handlePatientSelect = async (selectedOption) => {
      setSelectedPatient(selectedOption);
      
      if (selectedOption && selectedOption.originalData) {
          const data = selectedOption.originalData;
          const reason = data.problems || data.appointmentReason || data.reason || "";
          
          setClinicalInfo(prev => ({
              ...prev,
              chiefComplaints: reason
          }));

          const uhid = selectedOption.value;
          setLoadingHistory(true);
          try {
            const res = await customFetch(`/Prescriptions/history/${uhid}`);
            if (res.ok) {
                const historyData = await res.json();
                setPatientHistory(historyData);
            } else {
                setPatientHistory([]); 
            }
          } catch (err) {
              console.error("Failed to fetch history", err);
              setPatientHistory([]);
          } finally {
              setLoadingHistory(false);
          }
      } else {
          setClinicalInfo({ chiefComplaints: "", diagnosis: "", notes: "" });
          setPatientHistory([]); 
      }
  };

  const handleSelectFromList = (item) => {
      const uhid = item.uhidNo || item.uhiD_No;
      const formattedOption = {
          label: `[${item._type}] ${item.name} | UHID: ${uhid} | Mob: ${item.mobileNo}`,
          value: uhid,
          originalData: {
              ...item,
              type: item._type,
              refId: item._refId,
              srNo: item._srNo
          }
      };
      
      handlePatientSelect(formattedOption);
      setActivePanelTab('history');
  };

  const handleClinicalChange = (e) => {
    setClinicalInfo({ ...clinicalInfo, [e.target.name]: e.target.value });
  };

  const handleMedSelectChange = (name, opt) => {
    setCurrentMedicine({ ...currentMedicine, [name]: opt ? opt.value : "" });
  };

  const handleAddToCart = () => {
    if (!currentMedicine.selectedMed) {
      setError("Please select a medicine first.");
      return;
    }
    if (medicineCart.some(m => m.medID === currentMedicine.selectedMed.value)) {
      setError("This medicine is already added.");
      return;
    }

    const newMed = {
      medID: currentMedicine.selectedMed.value,
      medicineName: currentMedicine.selectedMed.medName,
      dosage: currentMedicine.dosage,
      duration: currentMedicine.duration,
      instructions: currentMedicine.instructions
    };

    setMedicineCart([...medicineCart, newMed]);
    setCurrentMedicine({ selectedMed: null, dosage: "", duration: "", instructions: "" });
  };

  const handleRemoveFromCart = (medID) => {
    setMedicineCart(medicineCart.filter(m => m.medID !== medID));
  };

  const handleSavePrescription = async () => {
    setError(""); setSuccess("");
    if (!selectedPatient) return setError("Please select a patient.");
    if (medicineCart.length === 0) return setError("Please add at least one medicine.");

    const payload = {
      uhidNo: selectedPatient.value,
      visitType: selectedPatient.originalData.type,
      visitId: selectedPatient.originalData.refId || 0,
      visitSrNo: selectedPatient.originalData.srNo || null,
      doctorId: Number(loggedInDoctorId),
      chiefComplaints: clinicalInfo.chiefComplaints,
      diagnosis: clinicalInfo.diagnosis,
      notes: clinicalInfo.notes,
      medicines: medicineCart.map(m => ({
        medID: m.medID,
        dosage: m.dosage,
        duration: m.duration,
        instructions: m.instructions
      }))
    };

   try {
    setLoading(true);
    const res = await customFetch(`/Prescriptions`, {
      method: "POST",
      body: JSON.stringify(payload)
    });
      if (!res.ok) throw new Error(await res.text() || "Failed to save prescription.");
      
      const result = await res.json();
      setSuccess("Prescription Saved Successfully!");
      
      const dataForPrint = {
        ...selectedPatient.originalData,
        ...payload,
        prescriptionId: result.prescriptionId,
        prescriptionDate: new Date(),
        patientName: selectedPatient.originalData.name,
        age: selectedPatient.originalData.age,
        gender: selectedPatient.originalData.gender,
        mobileNo: selectedPatient.originalData.mobileNo,
        address: selectedPatient.originalData.address,
        doctorName: loggedInDoctorName,
        medicines: medicineCart
      };

      setPrintData(dataForPrint);
      setShowPrintModal(true);

    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleModalClose = () => {
    setShowPrintModal(false);
    setSelectedPatient(null);
    setClinicalInfo({ chiefComplaints: "", diagnosis: "", notes: "" });
    setMedicineCart([]);
    setPatientHistory([]); 
    setActivePanelTab('today'); 
    setTodaysSearchText(""); 
    fetchTodaysPatients(); 
  };

  const vitals = selectedPatient?.originalData ? {
    BP: selectedPatient.originalData.bp || selectedPatient.originalData.BP || "-",
    Pulse: selectedPatient.originalData.pulses || selectedPatient.originalData.pulse || "-",
    SPO2: selectedPatient.originalData.oxy || selectedPatient.originalData.oxygen || "-",
    Temp: selectedPatient.originalData.temps || selectedPatient.originalData.temperature || "-",
    Weight: selectedPatient.originalData.weights || selectedPatient.originalData.weight || "-",
    Height: selectedPatient.originalData.heights || selectedPatient.originalData.height || "-",
    RBS: selectedPatient.originalData.rbs || selectedPatient.originalData.RBS || "-"
  } : null;

  return (
    <div className="dc-wrap">
      <div className="dc-layout-wrapper">
        
        {/* 70% Area: Main Form */}
        <div className="dc-main-panel">
          <div className="dc-card">
            {/* MAIN HEADER */}
            <div className="dc-header">
              <div className="dc-header-inner">
                <div className="dc-header-brand">
                  <div className="dc-header-icon"><i className="bi bi-file-medical"></i></div>
                  <span className="dc-header-title">E-Prescription</span>
                </div>
              </div>
            </div>

            <div className="dc-body">
              {error && <div className="dc-alert dc-alert-danger"><i className="bi bi-exclamation-triangle-fill"></i> {error}</div>}
              {success && <div className="dc-alert dc-alert-success"><i className="bi bi-check-circle-fill"></i> {success}</div>}

              <div ref={formRef}>
                
                {/* SUB SECTION 1 */}
                <div className="dc-section">
                  <div className="dc-section-title"><i className="bi bi-person-fill"></i><span>Patient Details</span></div>
                  <div className="dc-section-body">
                    <div className="dc-grid dc-grid-row-patient dc-mb2">
                      <div>
                        <label className="dc-label">Search Patient (Today's OPD/IPD) <span className="dc-required">*</span></label>
                        <AsyncSelect
                          autoFocus
                          cacheOptions
                          loadOptions={loadPatientOptions}
                          onChange={handlePatientSelect}
                          value={selectedPatient}
                          placeholder="Type Name, UHID or Mobile..."
                          isClearable
                          styles={selectStyles}
                          menuPortalTarget={document.body}
                          classNamePrefix="react-select"
                        />
                      </div>
                      <div>
                        <label className="dc-label">Selected Patient Info</label>
                        <div className="dc-input" style={{ backgroundColor: '#f8f9fa', display: 'flex', alignItems: 'center' }}>
                          {selectedPatient ? (
                            <span style={{ fontWeight: 600, color: 'var(--theme-color, #0088cc)' }}>
                              {selectedPatient.originalData.name} ({selectedPatient.originalData.age}/{selectedPatient.originalData.gender}) - {selectedPatient.originalData.type}
                            </span>
                          ) : (
                            <span style={{ color: '#a0aec0' }}>No patient selected</span>
                          )}
                        </div>
                      </div>
                    </div>

                    {vitals && (
                      <div className="dc-vitals-row">
                        <div className="vital-box"><span className="v-label">BP:</span> <span className="v-val">{vitals.BP}</span></div>
                        <div className="vital-box"><span className="v-label">Pulse:</span> <span className="v-val">{vitals.Pulse}</span></div>
                        <div className="vital-box"><span className="v-label">SpO2:</span> <span className="v-val">{vitals.SPO2}</span></div>
                        <div className="vital-box"><span className="v-label">Temp:</span> <span className="v-val">{vitals.Temp}</span></div>
                        <div className="vital-box"><span className="v-label">Weight:</span> <span className="v-val">{vitals.Weight}</span></div>
                        <div className="vital-box"><span className="v-label">Height:</span> <span className="v-val">{vitals.Height}</span></div>
                        <div className="vital-box"><span className="v-label">RBS:</span> <span className="v-val">{vitals.RBS}</span></div>
                      </div>
                    )}
                  </div>
                </div>

                {/* SUB SECTION 2 */}
                <div className="dc-section">
                  <div className="dc-section-title"><i className="bi bi-clipboard2-pulse"></i><span>Clinical Assessment</span></div>
                  <div className="dc-section-body">
                    <div className="dc-grid dc-grid-row-clinical dc-mb2">
                      <div>
                        <label className="dc-label">Chief Complaints (Auto-filled from Reg.)</label>
                        <textarea name="chiefComplaints" className="dc-textarea" placeholder="e.g. Fever, Cough... (Press Shift+Enter for new line)" value={clinicalInfo.chiefComplaints} onChange={handleClinicalChange} disabled={loading} />
                      </div>
                          <div>
                        <label className="dc-label">Prov Dx</label>
                        <textarea name="notes" className="dc-textarea" placeholder="e.g. Bed rest, Drink warm water (Press Shift+Enter for new line)" value={clinicalInfo.notes} onChange={handleClinicalChange} disabled={loading} />
                      </div>
                      <div>
                        <label className="dc-label">Diagnosis</label>
                        <textarea name="diagnosis" className="dc-textarea" placeholder="e.g. Viral Pyrexia (Press Shift+Enter for new line)" value={clinicalInfo.diagnosis} onChange={handleClinicalChange} disabled={loading} />
                      </div>
                    </div>
                  </div>
                </div>

                {/* SUB SECTION 3 */}
                <div className="dc-section">
                  <div className="dc-section-title"><i className="bi bi-capsule"></i><span>Prescribe Medicines</span></div>
                  <div className="dc-section-body">
                    <div className="dc-grid dc-grid-row-medicine dc-mb2 align-items-end">
                      <div id="medSearch">
                        <label className="dc-label">Search Medicine <span className="dc-required">*</span></label>
                        <AsyncSelect
                          cacheOptions
                          loadOptions={loadMedicineOptions}
                          value={currentMedicine.selectedMed}
                          onChange={val => setCurrentMedicine({...currentMedicine, selectedMed: val})}
                          placeholder="Type Medicine Name..."
                          isClearable
                          styles={selectStyles}
                          menuPortalTarget={document.body}
                          classNamePrefix="react-select"
                        />
                      </div>
                      <div>
                        <label className="dc-label">Dosage</label>
                        <CreatableSelect 
                          options={dosageOptions}
                          value={currentMedicine.dosage ? (dosageOptions.find(o => o.value === currentMedicine.dosage) || { label: currentMedicine.dosage, value: currentMedicine.dosage }) : null}
                          onChange={val => handleMedSelectChange('dosage', val)}
                          placeholder="Select or Type..."
                          isClearable
                          styles={selectStyles}
                          menuPortalTarget={document.body}
                          classNamePrefix="react-select"
                        />
                      </div>
                      <div>
                        <label className="dc-label">Duration</label>
                        <CreatableSelect 
                          options={durationOptions}
                          value={currentMedicine.duration ? (durationOptions.find(o => o.value === currentMedicine.duration) || { label: currentMedicine.duration, value: currentMedicine.duration }) : null}
                          onChange={val => handleMedSelectChange('duration', val)}
                          placeholder="Select or Type..."
                          isClearable
                          styles={selectStyles}
                          menuPortalTarget={document.body}
                          classNamePrefix="react-select"
                        />
                      </div>
                      <div>
                        <label className="dc-label">Instructions</label>
                        <CreatableSelect 
                          options={instructionOptions}
                          value={currentMedicine.instructions ? (instructionOptions.find(o => o.value === currentMedicine.instructions) || { label: currentMedicine.instructions, value: currentMedicine.instructions }) : null}
                          onChange={val => handleMedSelectChange('instructions', val)}
                          placeholder="Select or Type..."
                          isClearable
                          styles={selectStyles}
                          menuPortalTarget={document.body}
                          classNamePrefix="react-select"
                        />
                      </div>
                      <div>
                        <button id="btnAddMedicine" type="button" className="dc-btn-add" onClick={handleAddToCart} disabled={!currentMedicine.selectedMed}>
                          <i className="bi bi-plus-lg"></i> Add
                        </button>
                      </div>
                    </div>

                    <div className="dc-table-wrapper" style={{ marginTop: '10px' }}>
                      <table className="dc-table">
                        <thead>
                          <tr>
                            <th width="5%">#</th>
                            <th width="35%">Medicine Name</th>
                            <th width="15%">Dosage</th>
                            <th width="15%">Duration</th>
                            <th width="20%">Instructions</th>
                            <th width="10%" style={{ textAlign: 'center' }}>Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {medicineCart.length === 0 ? (
                            <tr><td colSpan="6" style={{ textAlign: 'center', color: '#a0aec0', padding: '15px 0' }}>No medicines added yet.</td></tr>
                          ) : (
                            medicineCart.map((med, idx) => (
                              <tr key={med.medID}>
                                <td>{idx + 1}</td>
                                <td style={{ fontWeight: 600 }}>{med.medicineName}</td>
                                <td>{med.dosage}</td>
                                <td>{med.duration}</td>
                                <td>{med.instructions}</td>
                                <td style={{ textAlign: 'center' }}>
                                  <button type="button" className="dc-btn-icon-danger" onClick={() => handleRemoveFromCart(med.medID)}>
                                    <i className="bi bi-trash"></i>
                                  </button>
                                </td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </table>
                    </div>

                  </div>
                </div>

                <div className="dc-form-footer">
                  <button type="button" className="dc-btn-submit" onClick={handleSavePrescription} disabled={loading || !selectedPatient || medicineCart.length === 0}>
                    {loading ? "Saving..." : <><i className="bi bi-printer me-1"></i> Save & Print Rx</>}
                  </button>
                </div>

              </div>
            </div>
          </div>
        </div>

        {/* 30% Area: Tabbed Panel (Today's Patients / History / Expiry Alerts) */}
        <div className="dc-history-panel">
          
          {/* TAB HEADERS */}
          <div className="panel-tabs">
            <div 
              className={`panel-tab ${activePanelTab === 'today' ? 'active' : ''}`} 
              onClick={() => setActivePanelTab('today')}
            >
              <i className="bi bi-people-fill"></i> Today
            </div>
            <div 
              className={`panel-tab ${activePanelTab === 'history' ? 'active' : ''}`} 
              onClick={() => setActivePanelTab('history')}
            >
              <i className="bi bi-clock-history"></i> History
            </div>
            {/* ✅ NEW EXPIRY TAB */}
            <div 
              className={`panel-tab ${activePanelTab === 'expiry' ? 'active' : ''}`} 
              onClick={() => setActivePanelTab('expiry')}
              title="Medicine Expiry Alerts"
            >
              <i className="bi bi-hourglass-split"></i> Expiry
              {filteredExpiryList.length > 0 && (
                <span className="badge bg-danger ms-1" style={{ fontSize: '9px', padding: '3px 5px' }}>{filteredExpiryList.length}</span>
              )}
            </div>
          </div>

          {/* TAB BODY */}
          <div className="history-body">
            
            {/* 1. TODAY'S PATIENTS TAB */}
            {activePanelTab === 'today' && (
              <>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                    <span style={{ fontSize: '11px', fontWeight: '600', color: '#475569' }}>
                        Registered Today <span className="badge bg-primary ms-1">{filteredTodaysPatients.length}</span>
                    </span>
                    <button className="btn btn-sm btn-light border" onClick={fetchTodaysPatients} disabled={loadingTodays} style={{ fontSize: '10px', padding: '2px 8px' }}>
                        <i className={`bi bi-arrow-clockwise ${loadingTodays ? 'spinning' : ''}`}></i> Refresh
                    </button>
                </div>

                <div style={{ marginBottom: '12px' }}>
                   <div className="search-input-wrapper">
                      <i className="bi bi-search search-icon"></i>
                      <input 
                          type="text" 
                          className="form-control-custom search-input" 
                          placeholder="Search Name or UHID..." 
                          value={todaysSearchText}
                          onChange={(e) => setTodaysSearchText(e.target.value)}
                      />
                   </div>
                </div>

                {loadingTodays ? (
                    <div className="history-placeholder">
                      <div className="spinner-border spinner-border-sm text-primary mb-2" role="status"></div>
                      <div>Loading patients...</div>
                    </div>
                ) : filteredTodaysPatients.length === 0 ? (
                    <div className="history-placeholder">
                      <i className="bi bi-person-x" style={{ fontSize: '24px', opacity: 0.5, marginBottom: '10px', display: 'block' }}></i>
                      {todaysSearchText ? "No matching patients found." : "No patients registered today."}
                    </div>
                ) : (
                    filteredTodaysPatients.map((p, idx) => (
                        <div key={idx} className="patient-list-card" onClick={() => handleSelectFromList(p)}>
                            <div className="plc-header">
                                <span className="plc-name">
                                    {p.name} <span style={{fontWeight:'normal', fontSize:'10px', color:'#64748b'}}>({p.age}/{p.gender?.charAt(0)})</span>
                                </span>
                                <span className={`plc-type ${(p._type || '').toLowerCase()}`}>{p._type}</span>
                            </div>
                            <div className="plc-uhid"><i className="bi bi-person-vcard"></i> {p.uhidNo || p.uhiD_No}</div>
                            <div className="plc-footer">
                                <span><i className="bi bi-telephone"></i> {p.mobileNo || "-"}</span>
                                <span className="plc-doc-badge">
                                    <i className="bi bi-person-badge"></i> {p.doctorName || "Doctor"}
                                </span>
                            </div>
                        </div>
                    ))
                )}
              </>
            )}

            {/* 2. PATIENT HISTORY TAB */}
            {activePanelTab === 'history' && (
              <>
                {!selectedPatient ? (
                  <div className="history-placeholder">
                    <i className="bi bi-search" style={{ fontSize: '24px', opacity: 0.5, marginBottom: '10px', display: 'block' }}></i>
                    Select a patient to view previous records.
                  </div>
                ) : loadingHistory ? (
                  <div className="history-placeholder">
                    <div className="spinner-border spinner-border-sm text-primary mb-2" role="status"></div>
                    <div>Loading history...</div>
                  </div>
                ) : patientHistory.length === 0 ? (
                  <div className="history-placeholder">
                    No previous prescriptions found for this patient.
                  </div>
                ) : (
                  patientHistory.map((record, index) => (
                    <div key={index} className="theme-glass-card">
                      <div className="glass-header">
                        <span className="glass-date">
                          <i className="bi bi-calendar-check"></i> {new Date(record.prescriptionDate).toLocaleDateString()}
                        </span>
                        <span className="glass-doc">
                          <i className="bi bi-person-badge"></i> {record.doctorName}
                        </span>
                      </div>
                      <div className="glass-body">
                          {record.chiefComplaints && (
                            <div className="glass-row">
                              <span className="glass-label">C/O:</span> {record.chiefComplaints}
                            </div>
                          )}
                          {record.notes && (
                            <div className="glass-row">
                              <span className="glass-label">Prov Dx:</span> {record.notes}
                            </div>
                          )}
                          {record.diagnosis && (
                            <div className="glass-row">
                              <span className="glass-label">Dx:</span> {record.diagnosis}
                            </div>
                          )}
                        
                          {record.medicines && record.medicines.length > 0 && (
                            <div className="glass-meds">
                              <div className="glass-meds-title">Medicines Prescribed:</div>
                              <ul className="glass-med-list">
                                {record.medicines.map((med, midx) => (
                                  <li key={midx}>
                                    <strong>{med.medicineName}</strong> - {med.dosage} <em>({med.duration})</em>
                                    {med.instructions && <div className="glass-med-inst">{med.instructions}</div>}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                      </div>
                    </div>
                  ))
                )}
              </>
            )}

            {/* 3. ✅ EXPIRY ALERTS TAB */}
            {activePanelTab === 'expiry' && (
              <>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                  <span style={{ fontSize: '11px', fontWeight: '600', color: '#475569' }}>
                    Expiring In:
                  </span>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <select 
                      className="expiry-filter-select"
                      value={expiryDaysFilter}
                      onChange={(e) => setExpiryDaysFilter(Number(e.target.value))}
                    >
                      <option value={30}>Next 30 Days</option>
                      <option value={60}>Next 2 Months</option>
                      <option value={90}>Next 3 Months</option>
                      <option value={180}>Next 6 Months</option>
                    </select>
                    {expiryLoading ? (
                      <div className="spinner-border spinner-border-sm text-danger" style={{width: '12px', height: '12px'}}></div>
                    ) : (
                      <i className="bi bi-arrow-clockwise" style={{ cursor: 'pointer', color: 'var(--theme-color, #0088cc)', fontSize: '12px' }} onClick={fetchExpiryData}></i>
                    )}
                  </div>
                </div>

                <div className="expiry-items">
                  {!expiryLoading && filteredExpiryList.length === 0 ? (
                    <div className="history-placeholder" style={{ background: '#f0fdf4', border: '1px dashed #bbf7d0', borderRadius: '6px', color: '#16a34a' }}>
                      <i className="bi bi-check-circle-fill" style={{ fontSize: '20px', display: 'block', marginBottom: '8px' }}></i>
                      All Good! No items expiring in selected period.
                    </div>
                  ) : (
                    filteredExpiryList.map((item, index) => {
                      const daysLeft = item.daysRemaining ?? item.DaysRemaining;
                      const isCritical = daysLeft <= 30;
                      return (
                        <div key={index} className={`expiry-card ${isCritical ? 'critical' : 'warning'}`}>
                          <div className="ec-left">
                            <div className="ec-name">{item.medicine || item.Medicine}</div>
                            <div className="ec-batch">Batch: {item.batchNo || item.BatchNo} | Stock: {item.balancePieces || item.BalancePieces} Pcs</div>
                          </div>
                          <div className="ec-right">
                            <div className="ec-days">{daysLeft} Days</div>
                            <div className="ec-date">{new Date(item.expDate || item.ExpDate).toLocaleDateString('en-IN', {month: 'short', year: 'numeric'})}</div>
                          </div>
                        </div>
                      )
                    })
                  )}
                </div>
              </>
            )}

          </div>
        </div>
      </div>

      {showPrintModal && printData && companyDetails && (
        <PrescriptionPrint 
           show={showPrintModal} 
           data={printData} 
           companyDetails={companyDetails} 
           onClose={handleModalClose} 
        />
      )}

      <style jsx>{`
        *, *::before, *::after { box-sizing: border-box; }
        .dc-wrap { padding: 0; font-family: system-ui, sans-serif; }
        
        .dc-layout-wrapper { display: flex; gap: 15px; align-items: flex-start; }
        .dc-main-panel { flex: 7; width: 70%; }
        
        .dc-history-panel { 
            flex: 3; 
            width: 30%; 
            background: linear-gradient(135deg, #e0f2fe 0%, #f8fafc 100%); 
            border-radius: 8px; 
            border: 1px solid #bae6fd; 
            box-shadow: 0 4px 15px rgba(0,0,0,0.03);
            display: flex; 
            flex-direction: column;
            position: sticky;
            top: 15px; 
            height: calc(100vh - 100px); 
            overflow: hidden;
        }

        /* TABS CSS */
        .panel-tabs {
            display: flex; 
            background: var(--theme-color, #0088cc); 
            border-radius: 8px 8px 0 0; 
            overflow: hidden;
        }
        .panel-tab {
            flex: 1; 
            padding: 10px 2px; 
            text-align: center; 
            color: rgba(255,255,255,0.7); 
            font-size: 11px; 
            font-weight: 600; 
            cursor: pointer; 
            transition: 0.3s; 
            display: flex; 
            align-items: center; 
            justify-content: center; 
            gap: 4px;
        }
        .panel-tab:hover { background: rgba(255,255,255,0.1); color: white; }
        .panel-tab.active { background: white; color: var(--theme-color, #0088cc); }

        .history-body { padding: 15px; overflow-y: auto; flex: 1; }
        .history-placeholder { text-align: center; color: #64748b; font-size: 12px; padding: 30px 10px; font-weight: 500; }
        
        @keyframes spin { 100% { transform: rotate(360deg); } }
        .spinning { animation: spin 1s linear infinite; display: inline-block; }

        /* SEARCH INPUT CSS */
        .search-input-wrapper {
            position: relative;
            display: flex;
            align-items: center;
        }
        .search-icon {
            position: absolute;
            left: 10px;
            color: #94a3b8;
            font-size: 12px;
        }
        .search-input {
            width: 100%;
            height: 30px;
            padding-left: 28px;
            border-radius: 6px;
            border: 1px solid #cbd5e1;
            font-size: 11px;
            outline: none;
            transition: border-color 0.2s;
        }
        .search-input:focus {
            border-color: var(--theme-color, #0088cc);
        }

        /* PATIENT LIST CARD CSS */
        .patient-list-card {
            background: white; 
            border: 1px solid #bae6fd; 
            border-radius: 6px; 
            padding: 10px 12px; 
            margin-bottom: 10px; 
            cursor: pointer; 
            transition: 0.2s; 
            box-shadow: 0 2px 4px rgba(0,0,0,0.02);
        }
        .patient-list-card:hover { 
            transform: translateY(-2px); 
            box-shadow: 0 4px 10px rgba(0,136,204,0.15); 
            border-color: var(--theme-color, #0088cc); 
        }
        .plc-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 6px; }
        .plc-name { font-weight: 700; color: #0f172a; font-size: 12px; display: flex; gap: 4px; align-items: center; }
        .plc-uhid { color: var(--theme-color, #0088cc); font-size: 11px; font-weight: 600; }
        .plc-type { font-weight: 700; padding: 2px 6px; border-radius: 4px; font-size: 9px; }
        .plc-type.opd { background: #dcfce7; color: #166534; border: 1px solid #bbf7d0; }
        .plc-type.ipd { background: #e0e7ff; color: #3730a3; border: 1px solid #c7d2fe; }
        .plc-footer { display: flex; justify-content: space-between; align-items: center; font-size: 10px; color: #64748b; margin-top: 8px; border-top: 1px dashed #e2e8f0; padding-top: 6px; }

        .plc-doc-badge {
            background: #e0f2fe; 
            color: #0284c7; 
            padding: 3px 8px;
            border-radius: 12px; 
            font-weight: 600;
            display: inline-flex;
            align-items: center;
            gap: 4px;
        }
        .plc-doc-badge i { color: #0284c7; }

        /* Theme-Bound Glassmorphism Cards (History) */
        .theme-glass-card {
            background: color-mix(in srgb, var(--theme-color, #0088cc) 8%, white 90%);
            backdrop-filter: blur(12px);
            border: 1px solid color-mix(in srgb, var(--theme-color, #0088cc) 30%, transparent);
            box-shadow: 0 4px 12px rgba(0, 0, 0, 0.04);
            border-radius: 8px;
            margin-bottom: 15px;
            overflow: hidden;
            transition: transform 0.2s, box-shadow 0.2s;
        }
        .theme-glass-card:hover { 
            transform: translateY(-2px); 
            box-shadow: 0 6px 15px color-mix(in srgb, var(--theme-color, #0088cc) 15%, transparent);
        }
        .glass-header { 
            display: flex; justify-content: space-between; padding: 8px 12px; 
            background: color-mix(in srgb, var(--theme-color, #0088cc) 80%, white); 
            border-bottom: 1px solid color-mix(in srgb, var(--theme-color, #0088cc) 40%, transparent); 
            font-size: 10.5px; font-weight: 600; color: white; 
        }
        .glass-doc { color: white; }
        .glass-body { padding: 10px 12px; font-size: 11px; color: #334155; }
        .glass-row { margin-bottom: 6px; line-height: 1.4; }
        .glass-label { font-weight: 600; color: var(--theme-color, #0088cc); margin-right: 4px; }
        .glass-meds { margin-top: 10px; padding-top: 8px; border-top: 1px dashed color-mix(in srgb, var(--theme-color, #0088cc) 40%, transparent); }
        .glass-meds-title { font-weight: 600; color: var(--theme-color, #0088cc); margin-bottom: 6px; font-size: 10.5px;}
        .glass-med-list { list-style: none; padding: 0; margin: 0; }
        .glass-med-list li { 
            background: rgba(255,255,255,0.7); 
            padding: 6px 8px; border-radius: 4px; margin-bottom: 5px; 
            border: 1px solid color-mix(in srgb, var(--theme-color, #0088cc) 20%, transparent); 
        }
        .glass-med-list strong { color: var(--theme-color, #0088cc); font-weight: 700;}
        .glass-med-inst { font-size: 10px; color: #64748b; margin-top: 2px; }

        /* ✅ NEW EXPIRY CSS */
        .expiry-filter-select {
          background: #ffffff;
          border: 1px solid #cbd5e1;
          border-radius: 4px;
          color: #0f172a;
          font-size: 10px;
          font-weight: 600;
          padding: 3px 6px;
          outline: none;
          cursor: pointer;
        }
        .expiry-items { display: flex; flex-direction: column; gap: 8px; }
        .expiry-card {
          display: flex; justify-content: space-between; align-items: center;
          padding: 8px 10px; border-radius: 6px; border: 1px solid transparent;
          background: white; cursor: pointer; transition: transform 0.2s, box-shadow 0.2s;
        }
        .expiry-card:hover { transform: translateY(-1px); box-shadow: 0 4px 6px rgba(0,0,0,0.05); }
        .expiry-card.critical { background: #fef2f2; border-color: #fecaca; }
        .expiry-card.warning { background: #fffbeb; border-color: #fde68a; }

        .ec-left { display: flex; flex-direction: column; gap: 2px; flex: 1; }
        .ec-name { font-size: 12px; font-weight: 700; color: #0f172a; }
        .ec-batch { font-size: 9px; font-weight: 600; color: #64748b; }

        .ec-right { display: flex; flex-direction: column; align-items: flex-end; gap: 2px; }
        .ec-days { 
          font-size: 10px; font-weight: 800; 
          padding: 2px 6px; border-radius: 4px;
        }
        .critical .ec-days { background: #dc2626; color: white; animation: pulse 2s infinite; }
        .warning .ec-days { background: #f59e0b; color: white; }
        .ec-date { font-size: 9px; font-weight: 700; color: #64748b; text-transform: uppercase; }
        @keyframes pulse {
          0% { box-shadow: 0 0 0 0 rgba(220, 38, 38, 0.4); }
          70% { box-shadow: 0 0 0 4px rgba(220, 38, 38, 0); }
          100% { box-shadow: 0 0 0 0 rgba(220, 38, 38, 0); }
        }

        /* Form CSS */
        .dc-card { background: #fff; border-radius: 6px; box-shadow: 0 1px 8px rgba(0,0,0,0.06); border: 1px solid #e0e6ed; overflow: visible; height: 100%;}
        .dc-header { background: var(--theme-color, #0088cc); padding: 6px 12px; border-radius: 6px 6px 0 0; border-bottom: 2px solid rgba(0,0,0,0.1); }
        .dc-header-inner { display: flex; align-items: center; }
        .dc-header-brand { display: flex; align-items: center; gap: 6px; color: white; font-weight: 600; font-size: 13px; }
        .dc-body { padding: 10px 12px; }
        .dc-section { background: #fff; border: 1px solid #e0e6ed; border-radius: 6px; margin-bottom: 12px; overflow: visible; }
        
        .dc-section-title { 
            background: #f0f4f8; 
            padding: 6px 12px; 
            display: flex; 
            align-items: center; 
            gap: 6px; 
            border-bottom: 2px solid var(--theme-color, #0088cc); 
            border-radius: 5px 5px 0 0; 
            color: #2c3e50; 
            font-size: 12px; 
            font-weight: 600; 
        }
        .dc-section-title i { color: var(--theme-color, #0088cc); font-size: 13px; }
        
        .dc-section-body { padding: 10px 12px; background: #fff; border-radius: 0 0 5px 5px; }
        
        .dc-grid { display: grid; gap: 8px; align-items: start; }
        .dc-grid-row-patient { grid-template-columns: 2fr 1fr; }
        .dc-grid-row-clinical { grid-template-columns: repeat(3, 1fr); }
        .dc-grid-row-medicine { grid-template-columns: 3fr 1.5fr 1.5fr 2fr 80px; align-items: end; }
        .dc-mb2 { margin-bottom: 8px; }

        .dc-vitals-row { display: flex; flex-wrap: wrap; gap: 12px; padding: 8px 12px; background: #e0f2fe; border-radius: 4px; border: 1px solid #bae6fd; margin-top: 10px;}
        .vital-box { font-size: 11px; display: flex; gap: 4px; }
        .v-label { color: #0284c7; font-weight: 600; }
        .v-val { color: #0f172a; font-weight: 700; }

        .dc-label { display: block; font-size: 10px; font-weight: 600; color: #2c3e50; margin-bottom: 3px; }
        .dc-required { color: #dc2626; }
        
        .dc-input, .dc-textarea { width: 100%; height: 26px; min-height: 26px; padding: 2px 6px; border: 1px solid #cbd5e0; border-radius: 4px; font-size: 11px; color: #2c3e50; outline: none; }
        .dc-input:focus, .dc-textarea:focus { border-color: var(--theme-color, #0088cc); box-shadow: 0 0 0 2px rgba(0,136,204,0.15); }
        .dc-textarea { resize: vertical; height: auto; min-height: 40px; padding-top: 4px; }
        
        .dc-btn-add { height: 26px; width: 100%; background: #10b981; color: white; border: none; border-radius: 4px; font-size: 11px; font-weight: 600; cursor: pointer; display: flex; align-items: center; justify-content: center; gap: 4px; transition: opacity 0.2s; }
        .dc-btn-add:hover:not(:disabled) { opacity: 0.9; }
        .dc-btn-add:disabled { opacity: 0.5; cursor: not-allowed; }

        .dc-form-footer { margin-top: 6px; padding-top: 8px; border-top: 1px solid #e0e6ed; text-align: right; }
        .dc-btn-submit { height: 26px; padding: 0 14px; background: var(--theme-color, #0088cc); color: white; border: none; border-radius: 4px; font-size: 11px; font-weight: 600; cursor: pointer; transition: opacity 0.2s; }
        .dc-btn-submit:hover:not(:disabled) { opacity: 0.9; }
        .dc-btn-submit:disabled { opacity: 0.6; cursor: not-allowed; }

        .dc-alert { padding: 6px 10px; border-radius: 4px; font-size: 11px; margin-bottom: 10px; display: flex; align-items: center; gap: 6px; }
        .dc-alert-danger { background: #fee2e2; color: #b91c1c; border: 1px solid #fecaca; }
        .dc-alert-success { background: #dcfce7; color: #15803d; border: 1px solid #bbf7d0; }

        .dc-table-wrapper { border: 1px solid #e0e6ed; border-radius: 4px; overflow: hidden; }
        .dc-table { width: 100%; border-collapse: collapse; font-size: 11px; }
        .dc-table th { background: #f1f5f9; padding: 6px; text-align: left; font-weight: 600; color: #475569; border-bottom: 1px solid #e0e6ed; }
        .dc-table td { padding: 6px; border-bottom: 1px solid #f1f5f9; color: #334155; vertical-align: middle; }
        .dc-table tbody tr:hover { background: #f8fafc; }
        .dc-table tbody tr:last-child td { border-bottom: none; }
        .dc-btn-icon-danger { background: transparent; border: none; color: #ef4444; cursor: pointer; padding: 2px 4px; border-radius: 3px; }
        .dc-btn-icon-danger:hover { background: #fee2e2; }

        @media (max-width: 900px) {
          .dc-layout-wrapper { flex-direction: column; }
          .dc-main-panel, .dc-history-panel { width: 100%; }
          .dc-history-panel { max-height: 400px; }
          
          .dc-grid-row-patient, .dc-grid-row-clinical { grid-template-columns: 1fr; }
          .dc-grid-row-medicine { grid-template-columns: 1fr 1fr; align-items: start; }
          .dc-grid-row-medicine > div:nth-child(1) { grid-column: span 2; }
          .dc-grid-row-medicine > div:nth-child(5) { grid-column: span 2; }
        }
      `}</style>
    </div>
  );
};

export default PrescriptionCreate;